/**
 * The palette of the two admin screens — User Management and Companies & Bases.
 *
 * Those two started out picking their own greys, which left three different
 * "muted text" colours across the settings drawer and a selection blue that
 * appears nowhere in the theme. The values here are not new: they are the ones
 * Version Update (updateDatabase.js) already uses, so the admin screens join
 * that family rather than opening a fourth one. The two accents come from the
 * MUI theme in index.js.
 *
 * Same idea, and same shape, as snapshotEditor/panelPalette.js.
 */

import { makeStyles } from "@material-ui/core/styles";

/** The page itself — matches the drawer paper, so panels read as raised. */
export const PAGE_BG = "#404040";

/** A panel: the detail pane, a section card, the list surface. */
export const PANEL_BG = "#4b4b4b";

/** Recessed: search boxes, scrolling lists, anything that reads as "inside". */
export const INSET_BG = "#3a3a3a";

/** Hairline around panels and controls. */
export const BORDER = "#555";

/** A stronger edge, for a control that has to be visible against PANEL_BG. */
export const BORDER_STRONG = "#6a6a6a";

/** Secondary text: hints, counts, metadata under a title. */
export const MUTED = "#9aa0a0";

/** Selection, tab indicator, primary action — the theme's primary.main. */
export const ACCENT = "#4eabb1";

/** The theme's secondary.main, for a filled chip on the accent. */
export const ACCENT_SOFT = "#9bcec0";

/** Destructive: the delete icon, and the rim of a dangerous section. */
export const DANGER = "#e57373";

/** Row fill behind the selected item in a list. */
export const ROW_SELECTED_BG = "#565656";

/**
 * The frame both admin screens are built on.
 *
 * User Management and Companies & Bases are the same object twice: a header with
 * one primary action, a list that scrolls on its own, a tabbed detail pane, and
 * a pinned action bar. They were carrying ~80 lines of identical style each,
 * which is how two screens that are supposed to look alike stop looking alike —
 * a padding tuned on one never reaches the other.
 *
 * Spread it into a screen's own `makeStyles` and add what is genuinely local:
 *
 *     const useStyles = makeStyles(() => ({ ...adminScreenFrame, myOwnThing: {} }));
 *
 * A plain object rather than a second hook, so a screen still has ONE class map
 * and nothing has to be looked up in two places at the point of use.
 */
/**
 * How wide the working area is allowed to get.
 *
 * Both screens are a fixed-width list beside a detail pane, and the pane was the
 * only thing taking up the slack: on a wide monitor it ran past 1600px, which
 * puts a checkbox and a two-word label at opposite ends of a row, and the Save
 * button a screen away from the form it saves. Nothing in either pane reads
 * better for being wider.
 *
 * The detail pane's width is the decision; the cap is derived from it, so one
 * number moves the header, the columns and the action bar together. 660px still
 * holds the two-column permission grid (two 260px columns and their gap) and the
 * widest action bar — Companies & Bases', with three buttons — on one line.
 *
 * The list keeps its width: it is sized to its longest row ("129 of 131 bases ·
 * 6 permissions" under a username), not to the screen, so it never stretched.
 *
 * The cap goes on the header and the body together so everything stops on the
 * same edge. Everything inside a tab inherits it, which is why no individual tab
 * has to think about it.
 */
const LIST_WIDTH = 280;
const DETAIL_WIDTH = 660;
// list + the 1.5rem gap between the columns + detail + 2rem padding each side.
const SCREEN_MAX_WIDTH = `calc(${LIST_WIDTH}px + 1.5rem + ${DETAIL_WIDTH}px + 4rem)`;

export const adminScreenFrame = {
  // The drawer paper starts at 46px; matching it exactly is what keeps the
  // action bar pinned instead of scrolling away with the form.
  root: {
    marginLeft: "21.5rem",
    height: "calc(100vh - 46px)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    backgroundColor: PAGE_BG,
    color: "white",
    textAlign: "left",
  },
  centred: { flex: 1, display: "flex", alignItems: "center", justifyContent: "center" },

  // Header
  header: {
    display: "flex",
    alignItems: "center",
    gap: "1rem",
    padding: "1.25rem 2rem 1rem 2rem",
    flexShrink: 0,
    width: "100%",
    maxWidth: SCREEN_MAX_WIDTH,
    boxSizing: "border-box",
  },
  // 500 rather than the 540 both screens used to ask for: no face in the stack
  // has a 540, so it was already rendering as 500 — this just says so.
  title: { fontSize: "1.5rem", fontWeight: 500 },
  headerSpacer: { flexGrow: 1 },
  headerSelect: { minWidth: "240px" },

  body: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    gap: "1.5rem",
    padding: "0 2rem 1.5rem 2rem",
    width: "100%",
    maxWidth: SCREEN_MAX_WIDTH,
    boxSizing: "border-box",
  },

  // List column
  listColumn: {
    width: `${LIST_WIDTH}px`,
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    backgroundColor: PANEL_BG,
    border: `1px solid ${BORDER}`,
    borderRadius: "4px",
  },
  listHeader: {
    padding: "0.5rem 0.75rem",
    borderBottom: `1px solid ${BORDER}`,
    flexShrink: 0,
  },
  listLabel: {
    fontSize: "0.72rem",
    letterSpacing: 0.5,
    textTransform: "uppercase",
    color: MUTED,
  },
  // Only when the header also carries a search box — otherwise the label is the
  // whole header and needs no gap under it.
  listLabelSpaced: { marginBottom: "0.25rem" },
  listScroller: { flex: 1, minHeight: 0, overflowY: "auto", padding: "4px" },

  /**
   * One row, both screens. The accent rail on the left is the selection, rather
   * than a border all the way round — a full outline on a 280px column reads as
   * another panel inside the panel.
   */
  listRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "6px 8px 6px 10px",
    borderRadius: "3px",
    cursor: "pointer",
    borderLeft: "3px solid transparent",
    "&:hover": { backgroundColor: "#454545" },
  },
  listRowSelected: { backgroundColor: ROW_SELECTED_BG, borderLeftColor: ACCENT },
  rowTitle: { fontSize: "0.88rem", display: "flex", alignItems: "center" },
  rowMeta: { fontSize: "0.68rem", color: MUTED, marginTop: "1px" },

  /**
   * State badges. One language across both lists: filled for the strongest
   * state, outlined for the rest, and nothing at all for the ordinary case —
   * a badge on every row is a badge on none.
   */
  chip: {
    fontSize: "0.6rem",
    padding: "0 5px",
    borderRadius: "8px",
    marginLeft: "6px",
    lineHeight: "14px",
    whiteSpace: "nowrap",
  },
  chipFilled: { backgroundColor: ACCENT, color: "#10312f", fontWeight: 600 },
  chipOutline: { border: `1px solid ${ACCENT}`, color: ACCENT },

  // Detail pane
  detailColumn: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    backgroundColor: PANEL_BG,
    border: `1px solid ${BORDER}`,
    borderRadius: "4px",
  },
  detailEmpty: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: MUTED,
  },
  detailTitle: {
    padding: "0.75rem 1.25rem 0 1.25rem",
    fontSize: "1.05rem",
    fontWeight: 600,
    flexShrink: 0,
  },
  // 0.5rem here + MUI's own 12px on a Tab puts the first label at 1.25rem, in
  // line with the title above it. Without it the rail sits a tab's padding out.
  tabsBar: {
    borderBottom: `1px solid ${BORDER}`,
    flexShrink: 0,
    minHeight: "40px",
    padding: "0 0.5rem",
  },
  tab: { minHeight: "40px", minWidth: 0, fontSize: "0.75rem", textTransform: "none" },
  // For a detail pane with nothing to tab between — the create form. Without it
  // the title floats over the body, and the pane looks unlike itself the moment
  // a company is selected instead.
  detailRule: { borderBottom: `1px solid ${BORDER}`, marginTop: "0.75rem", flexShrink: 0 },
  tabBody: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    padding: "1.25rem",
    display: "flex",
    flexDirection: "column",
  },
  footer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "0.75rem 1.25rem",
    borderTop: `1px solid ${BORDER}`,
    flexShrink: 0,
  },
  footerSpacer: { flexGrow: 1 },

  // Inside a tab
  hint: { fontSize: "0.75rem", color: MUTED, marginBottom: "0.75rem" },
  subTitle: {
    fontSize: "0.85rem",
    fontWeight: 600,
    marginTop: "0.25rem",
    marginBottom: "0.35rem",
  },
  field: { width: "100%", maxWidth: "380px", marginBottom: "1rem" },
  block: { marginBottom: "1.5rem" },

  /**
   * A checkbox with a label and a line of explanation under it. Named for what
   * it is, because four different things wear it — permissions, the base
   * narrowing switch, the editor-tab switches and their checklists — and while
   * it was called `permissionItem` tuning one of them silently moved the rest.
   *
   * The label sits on its own line above the hint: as one run of text the longer
   * hints wrapped mid-sentence and no two rows in the grid lined up.
   */
  checkRow: { display: "flex", alignItems: "flex-start", cursor: "pointer", padding: "3px 0" },
  // Optical alignment with the checkbox glyph, which is inset in its own box.
  checkText: { paddingTop: "7px" },
  checkLabel: { fontSize: "0.82rem", display: "block", lineHeight: 1.3 },
  checkHint: { fontSize: "0.7rem", color: MUTED, display: "block", lineHeight: 1.4 },

  // Two readable columns that drop to one when the pane is narrow.
  splitColumns: { display: "flex", gap: "20px", flexWrap: "wrap" },
  splitColumn: { flex: "1 1 260px", minWidth: 0 },
};

/**
 * Props for a search box that sits inside a toolbar which already has a border
 * under it. Without this the input draws its own underline a few pixels above
 * that border, and the pair reads as a mistake.
 */
export const SEARCH_INPUT_PROPS = { disableUnderline: true };

/**
 * The checklist chrome: a toolbar (search, count, All/None) over a scrolling
 * list of checkboxes.
 *
 * Two components wear it — SelectorBases and SelectorCompanies — and they sit
 * next to each other on the same screen, so the shell lives here instead of
 * each one drawing its own. `fill: true` makes the list take the height it is
 * given rather than a fixed maximum, which is what a tab body wants.
 */
export const useChecklistStyles = makeStyles(() => ({
  wrapper: {
    border: `1px solid ${BORDER}`,
    borderRadius: "4px",
    backgroundColor: INSET_BG,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  },
  wrapperFill: { flex: 1 },
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "6px 8px",
    borderBottom: `1px solid ${BORDER}`,
    flexShrink: 0,
  },
  search: { flexGrow: 1 },
  count: {
    fontSize: "0.72rem",
    color: MUTED,
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
  },
  scroller: { overflowY: "auto", textAlign: "left", minHeight: 0 },
  scrollerFill: { flex: 1 },
  scrollerCapped: { maxHeight: "260px" },
  groupHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "3px 8px",
    backgroundColor: "#333",
    position: "sticky",
    top: 0,
    zIndex: 1,
  },
  groupTitle: { fontSize: "0.75rem", fontWeight: 600, color: MUTED },
  row: {
    display: "flex",
    alignItems: "center",
    padding: "0 8px",
    cursor: "pointer",
    "&:hover": { backgroundColor: "#454545" },
  },
  rowLabel: { fontSize: "0.8rem", color: "#f0f0f0" },
  tag: {
    marginLeft: "8px",
    fontSize: "0.62rem",
    padding: "1px 5px",
    borderRadius: "3px",
    border: `1px solid ${ACCENT}`,
    color: ACCENT_SOFT,
    whiteSpace: "nowrap",
  },
  empty: { padding: "16px", fontSize: "0.8rem", color: MUTED },
  linkButton: {
    fontSize: "0.68rem",
    minWidth: 0,
    padding: "0 4px",
    color: ACCENT,
  },
}));
